import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { InputField } from "@/components/ui/FormFields";
import {
  getMyEventOrganizer,
  registerEventOrganizer,
  updateMyEventOrganizer,
  inviteCoOrganizer,
} from "@/api/event-organizers";
import { ApiError } from "@/api/client";
import { MyTeamInvitesPanel, TeamPanel } from "./TeamPanel";
import { ResubmitPanel } from "./ResubmitPanel";
import { HostPeoplePanel } from "@/components/HostPeoplePanel";

export default function EventOrganizersLab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["event-organizer-me"],
    queryFn: getMyEventOrganizer,
    retry: false,
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["event-organizer-me"] });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Event Organizers"
        subtitle="Register an organizer profile, manage it, invite co-organizers."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Event Organizers" },
        ]}
      />

      <MyTeamInvitesPanel />
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : data ? (
        <Profile eo={data.data} onChange={refresh} />
      ) : (
        <RegisterForm onSuccess={refresh} />
      )}
    </div>
  );
}

function RegisterForm({ onSuccess }: { onSuccess: () => void }) {
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await registerEventOrganizer({
        displayName,
        bio: bio || undefined,
        avatarUrl: avatarUrl || undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 max-w-xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        Register as an event organizer
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Creates your profile + an{" "}
        <code className="font-mono">OrgMember(owner)</code> + a pending{" "}
        <code className="font-mono">VerificationRequest</code>.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <InputField
          label="Display name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={200}
        />
        <InputField
          label="Bio (optional)"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={2000}
        />
        <InputField
          label="Avatar URL (optional)"
          type="url"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
        />
        <Button
          type="submit"
          isLoading={submitting}
          disabled={displayName.trim().length < 2}
        >
          Register
        </Button>
        {error && (
          <p className="text-sm text-red-700 mt-2">{error}</p>
        )}
      </form>
    </section>
  );
}

function Profile({
  eo,
  onChange,
}: {
  eo: Awaited<ReturnType<typeof getMyEventOrganizer>>["data"];
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(eo.displayName);
  const [bio, setBio] = useState(eo.bio ?? "");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateMyEventOrganizer({ displayName, bio: bio || undefined });
      onChange();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900">
                {eo.displayName}
              </h2>
              <Badge variant={eo.isVerified ? "green" : "yellow"}>
                {eo.isVerified ? "verified" : "unverified"}
              </Badge>
            </div>
            <div className="text-xs text-gray-500 mt-1 font-mono">id: {eo.id}</div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setEditing(!editing)}
          >
            {editing ? "Cancel" : "Edit"}
          </Button>
        </div>

        {editing ? (
          <div className="space-y-3 max-w-xl">
            <InputField
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <InputField
              label="Bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
            />
            <Button onClick={save} isLoading={saving}>
              Save
            </Button>
          </div>
        ) : (
          <>
            {eo.bio && <p className="text-sm text-gray-700">{eo.bio}</p>}
            <div className="mt-3 flex gap-6 text-sm text-gray-600">
              <span>
                <strong>{eo.eventCount}</strong> events
              </span>
              <span>
                <strong>{eo.totalAttendees}</strong> attendees
              </span>
            </div>
          </>
        )}
      </section>

      {eo.verificationRequest && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="font-semibold text-gray-900 mb-2">
            Verification status
          </h3>
          <div className="text-sm text-gray-700">
            Current status: <Badge variant="blue">{eo.verificationRequest.status}</Badge>
          </div>
          {eo.verificationRequest.rejectReason && (
            <p className="text-sm text-red-700 mt-2">
              Reason: {eo.verificationRequest.rejectReason}
            </p>
          )}
          {eo.verificationRequest.reviewNotes && (
            <p className="text-sm text-gray-600 mt-1">
              Notes: {eo.verificationRequest.reviewNotes}
            </p>
          )}
          <ResubmitPanel />
        </section>
      )}

      <TeamPanel pageId={eo.id} />
      <HostPeoplePanel kind="event_organizer" id={eo.id} />
      <InviteCoOrganizer eventOrganizerId={eo.id} />
    </div>
  );
}

function InviteCoOrganizer({
  eventOrganizerId,
}: {
  eventOrganizerId: string;
}) {
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setFeedback(null);
    try {
      await inviteCoOrganizer(eventOrganizerId, {
        email,
        title: title || undefined,
      });
      setFeedback(`Invited ${email}`);
      setEmail("");
      setTitle("");
    } catch (err) {
      if (err instanceof ApiError) {
        setFeedback(`${err.code}: ${err.message}`);
      } else {
        setFeedback(err instanceof Error ? err.message : "Failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h3 className="font-semibold text-gray-900 mb-1">
        Invite a co-organizer
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Email must belong to an existing WeDesi account.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3 max-w-md">
        <InputField
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <InputField
          label="Title (optional)"
          placeholder="Co-organizer, Producer, …"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Button type="submit" isLoading={submitting} disabled={!email.trim()}>
          Send invite
        </Button>
        {feedback && (
          <p className="text-sm text-blue-700 mt-2">{feedback}</p>
        )}
      </form>
    </section>
  );
}
